import { LanguagesEnum } from '@/api/enums';
import { PhishingLanguagesEnum } from '@/api/enums/phishing-languages.enum';
import { FiltersType, ListingRequest, ListingResponse } from '@/api/types';
import {
  Campaign,
  CampaignMeta,
  CampaignStatusesEnum,
  CampaignTemplate,
  CampaignUserResult,
  SenderProfileType,
} from '@/client/campaigns';
import { Benchmark } from '@/client/campaigns/types/benchmarks.type';
import { CatalogueVariant } from '@/client/campaigns/types/catalogue.type';
import { SendingProfile } from '@/client/sender-profiles';
import { User } from '@/client/users';
import {
  ExcludeFormValue,
  TargetEntities,
  TargetEntitiesRequest,
  TargetFormValue,
  TranslationFunctionType,
} from '@/common/types';
import { TargetGroupStateType } from '@/utils/helpers';

export type CreateAutoPhishingCampaignRequest = {
  campaignTemplateId: string;
  launchDate: string | Date;
  sendByDate?: string | Date;
  targets?: TargetEntitiesRequest;
  excludes?: TargetEntitiesRequest;
};

export type UpdateLanguageGroupPayload = {
  launchDate?: string | Date;
  sendByDate?: string | Date;
  targets?: TargetEntitiesRequest;
  excludes?: TargetEntitiesRequest;
};

export type LanguageGroupResult = {
  languageGroupId: string;
  campaignIds: string[];
};

export type LanguageFailureNotice = {
  id: string;
  simulationName: string;
  created: string;
};

export type CampaignSyncFailedPayload = {
  id: string;
  accountId: string;
  name: string;
  error?: string;
  languageGroupId?: string | null;
};

export type CampaignSyncFinishedPayload = {
  id: string;
  accountId: string;
  name: string;
  languageGroupId?: string | null;
  removed?: boolean;
};

export type BulkLaunchCampaignsResponse = {
  launchedCampaignIds: string[];
  failedCampaignIds: string[];
};

export type CreateAutoPhishingCampaignsRequest = {
  period: number;
  campaignsCount: number;
};

export type PlaceholderSelectionCandidate = {
  id: string;
  name: string;
};

export type PlaceholderSignReasonCode =
  | 'least-used'
  | 'nth-least-used'
  | 'season';

export type PlaceholderSeasonSkipCode =
  | 'no-window'
  | 'no-template'
  | 'claimed-elsewhere';

export type PlaceholderSelectionPreview = {
  placeholderId: string;
  launchDate: string;
  sign: CampaignPhishingSignEnum;
  seasonName: string | null;
  candidates: PlaceholderSelectionCandidate[];
  fallbackCandidateId: string | null;
  signReasonCode?: PlaceholderSignReasonCode;
  signRank?: number | null;
  seasonSkipCode?: PlaceholderSeasonSkipCode | null;
  seasonSkipName?: string | null;
  excludedBenchmarkCount?: number;
};

export type PlaceholderLikelyPick = {
  placeholderId: string;
  templateId: string;
  templateName: string;
  generatedAt: string;
  explanation: string | null;
};

export type ListCampaignsRequest = ListingRequest;
export type ListCampaignUserResultsRequest = ListingRequest;
export type ListCampaignsResponse = ListingResponse<Campaign>;
export type ListCampaignTemplatesResponse = ListingResponse<CampaignTemplate>;
export type ListCampaignUserResultsResponse =
  ListingResponse<CampaignUserResult>;

export type CampaignFormValues = {
  emailTemplate?: string;
  landingPage?: string;
  targetGroup?: TargetGroupStateType;
  targets?: TargetFormValue[];
  excludes?: ExcludeFormValue[];
  sendingProfile?: SendingProfile;
  periodType?: CampaignEmailingType;
  sendByDate?: Date;
  launchDate?: Date;
  name?: string;
  language?: LanguagesEnum;
  difficulty?: number;
  senderProfileType?: SenderProfileType;
  clickedLinksPercentage?: number;
  submittedDataPercentage?: number;
  description?: string;
  encodeEmailTemplateImages?: boolean;
  useDynamicTarget?: boolean;
  meta?: CampaignMeta;
  phishingSign?: CampaignPhishingSignEnum;
  benchmark?: Benchmark;
  benchmarkSummary?: {
    clickedLinksPercentage: number;
    submittedDataPercentage: number;
    emailsReportedPercentage: number;
  };
  isAutoPhishing?: boolean;
  isPublished?: boolean;
  isWhitelistingTest?: boolean;
  completeAfter?: Date;
  thumbnail?: string;
  masterTemplate?: string | null;
};
export const CampaignEmailingEnum = {
  DRIP: 'drip',
  BLAST: 'blast',
};

export const CampaignActionsEnum = {
  LAUNCH: 'launch',
  COMPLETE: 'complete',
};

export type CampaignEmailingType =
  | typeof CampaignEmailingEnum.DRIP
  | typeof CampaignEmailingEnum.BLAST;

export type CampaignActionsType =
  | typeof CampaignActionsEnum.LAUNCH
  | typeof CampaignActionsEnum.COMPLETE;

export type UpdateCampaignAction = {
  campaignId: string;
  action: CampaignActionsType;
};

export type SaveCampaignRequest = {
  encodeEmailTemplateImages?: boolean;
  name?: string;
  launchDate?: Date;
  sendByDate?: Date | null;
  emailTemplate?: string;
  landingPage?: string;
  sendingProfile?: string;
  url?: string;
  account?: string;
  users?: string[] | null;
  branches?: string[];
  groups?: string[];
  targets?: TargetEntitiesRequest;
  excludes?: TargetEntitiesRequest;
  useDynamicTarget?: boolean;
  language?: LanguagesEnum;
  difficulty?: number;
  senderProfileType?: SenderProfileType;
  clickedLinksPercentage?: number;
  submittedDataPercentage?: number;
  description?: string;
  benchmark?: string;
  phishingSign?: CampaignPhishingSignEnum;
  isAutoPhishing?: boolean;
  isPublished?: boolean;
  isWhitelistingTest?: boolean;
  completeAfter?: Date;
  thumbnail?: string;
  masterTemplate?: string | null;
};

export type CloneCampaignRequest = {
  campaignId: string;
  account: string;
};

export type SendTestEmailFormValues = {
  emailType: 'admin' | 'custom';
  email?: string;
  firstName?: string;
  lastName?: string;
  position?: string;
  admin?: User;
  language?: LanguagesEnum;
};

export type SendTestEmail = {
  campaignId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  position?: string;
  emailTemplateId: string;
  sendingProfileId: string;
  landingPageId: string;
  encodeEmailTemplateImages?: boolean;
  language?: LanguagesEnum;
};

export type UpdateCampaign = {
  encodeEmailTemplateImages?: boolean;
  name?: string;
  launchDate?: Date;
  sendByDate?: Date | null;
  emailTemplate?: string;
  landingPage?: string;
  sendingProfile?: string;
  url?: string;
  account?: string;
  users?: string[] | null;
  branches?: string[];
  groups?: string[];
  targets?: TargetEntities;
  excludes?: TargetEntities;
  useDynamicTarget?: boolean;
  language?: LanguagesEnum;
  difficulty?: number;
  senderProfileType?: SenderProfileType;
  clickedLinksPercentage?: number;
  submittedDataPercentage?: number;
  description?: string;
  benchmark?: Benchmark;
  phishingSign?: CampaignPhishingSignEnum;
};

export type UpdateCampaignRequest = {
  updates: UpdateCampaign;
  campaignId: string;
};

export type MigrationCampaignRequest = {
  accountId: string;
  campaignIds: string[];
};

export type ExportCampaignsReportRequest = {
  filters: FiltersType | undefined;
  exportKey: string;
  columns?: string[];
};

export type CampaignRecommendationRequest = {
  count: number;
};

export type CampaignRecommendationFormState =
  Partial<CampaignRecommendationRequest>;

export type PlannedCampaign = {
  id: string;
  name: string;
  sign: string;
  explanation: string;
  launchDate: string; // ISO date
  projectedCompletionDate?: string; // ISO date
  seasonalContext: string;
  progressionRationale: string;
};

export type TimingAnalysis = {
  nextAvailableLaunchDate: string;
  totalPlanDuration?: number;
  seasonalConsiderations: string[];
  spacingRationale: string;
};

export type CampaignRecommendationResult = {
  analysis: string;
  numberOfTestedCampaigns: number;
  numberOfTestedSigns: number;
  signsPerformanceAnalysis: string;
  campaignPerformanceAnalysis: string;
  plannedCampaigns: PlannedCampaign[];
  timingAnalysis?: TimingAnalysis;
};

export type CampaignRecommendationResponse = {
  result: CampaignRecommendationResult;
};

export enum CampaignPhishingSignEnum {
  REWARDS = 'rewards',
  MEDIA = 'media',
  DO_THE_RIGHT_THING = 'do-the-right-thing',
  FEAR = 'fear',
  CURIOSITY = 'curiosity',
  CONTEXT = 'context',
  TRUST_OR_AUTHORITY = 'trust-or-authority',
  TIME_SENSITIVITY = 'time-sensitivity',
}

export const phishingSignsLabels = (t: TranslationFunctionType) => ({
  [CampaignPhishingSignEnum.REWARDS]: t('campaign.template.rewards'),
  [CampaignPhishingSignEnum.FEAR]: t('campaign.template.fear'),
  [CampaignPhishingSignEnum.CURIOSITY]: t('campaign.template.curiosity'),
  [CampaignPhishingSignEnum.TRUST_OR_AUTHORITY]: t(
    'campaign.template.trust-or-authority',
  ),
  [CampaignPhishingSignEnum.TIME_SENSITIVITY]: t(
    'campaign.template.time-sensitivity',
  ),
  [CampaignPhishingSignEnum.MEDIA]: t('campaign.template.media'),
  [CampaignPhishingSignEnum.CONTEXT]: t('campaign.template.context'),
  [CampaignPhishingSignEnum.DO_THE_RIGHT_THING]: t(
    'campaign.template.do-the-right-thing',
  ),
});

export type PhishingSignsSummary = {
  usedSigns: CampaignPhishingSignEnum[];
  missingSigns: CampaignPhishingSignEnum[];
};

export type SimulationsSummary = {
  activeCount: number;
  scheduledCount: number;
  draftCount: number;
  phishingSignsSummary: PhishingSignsSummary;
};

export type CampaignStatusCounts = {
  completedCount: number;
  activeCount: number;
  scheduledCount: number;
  draftCount: number;
  failedCount: number;
};

export type SimulationPlanData = {
  id?: string;
  campaignTemplateId?: string;
  difficulty?: number;
  phishingSign?: CampaignPhishingSignEnum;
  name?: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  targetGroup: TargetFormValue[] | undefined;
  excludes?: ExcludeFormValue[];
  isGeneratedWithAI?: boolean;
  language?: PhishingLanguagesEnum;
  variants?: CatalogueVariant[];
};

export type SimulationWithAssistancePlanData = {
  timePeriod: number;
  numberOfSimulations: number;
};

export type SimulationSchedulerItem = {
  id: string;
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  color?: string;
  disabled?: boolean;
  canDelete?: boolean;
  phishingSign?: CampaignPhishingSignEnum;
  difficulty?: number;
  campaignTemplateId?: string;
  targetGroup?: TargetEntities;
  excludes?: TargetEntities;
  isGeneratedWithAI?: boolean;
  status?: CampaignStatusesEnum;
  targetUsersCount: number;
  emailTemplateId?: string;
  sendingProfileId?: string;
  landingPageId?: string;
  language?: PhishingLanguagesEnum;
  languageGroupId?: string;
  sendIds?: string[];
  languages?: PhishingLanguagesEnum[];
  testSendId?: string;
  testSends?: TestEmailSend[];
};

export type TestEmailSend = {
  campaignId: string;
  language: PhishingLanguagesEnum;
  isFallback: boolean;
  emailTemplateId?: string;
  sendingProfileId?: string;
  landingPageId?: string;
};

export enum EmailProvider {
  MICROSOFT = 'microsoft',
  GOOGLE = 'google',
}

export const WhitelistingDomain = 'link120623.dk';
export const WhitelistingSimulationUrl = 'link120623.dk/*';
export const WhitelistingIPAddresses = [
  '91.233.64.66',
  '91.233.64.65',
  '91.233.64.64',
];

export const MailConnectorIPAddresses = [
  '3.75.105.111',
  '3.77.162.184',
  '3.120.101.167',
  '35.159.187.97',
];
